import React from "react";
import withStyles from "@material-ui/core/styles/withStyles";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import business from "../data/business.json";
import { visibleReviews } from "../data/reviews";

const styles = theme => ({
  section: {
    backgroundColor: "#FFF8EE",
    padding: "6rem 20px",
    [theme.breakpoints.down("sm")]: {
      padding: "4rem 16px"
    }
  },
  inner: {
    maxWidth: 1100,
    margin: "0 auto"
  },
  heading: {
    marginBottom: "3rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "2rem"
    }
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 24,
    listStyle: "none",
    padding: 0,
    margin: 0
  },
  card: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    border: "1px solid #F1E3CC",
    borderRadius: 12,
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.06)",
    padding: "1.75rem",
    textAlign: "left"
  },
  placeholder: {
    border: "2px dashed #EC9524"
  },
  placeholderBadge: {
    alignSelf: "flex-start",
    backgroundColor: "#212529",
    color: "#fff",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    borderRadius: 4,
    padding: "2px 8px",
    marginBottom: "0.75rem"
  },
  stars: {
    color: "#EC9524",
    marginBottom: "0.5rem",
    display: "flex"
  },
  quote: {
    flexGrow: 1,
    margin: 0,
    fontSize: "1.1rem",
    lineHeight: 1.6,
    color: "#212529",
    "&::before": {
      content: '"\\201C"',
      display: "block",
      fontFamily: "Pacifico",
      fontSize: "3rem",
      lineHeight: 1,
      height: "1.5rem",
      color: "#EC9524"
    }
  },
  attribution: {
    marginTop: "1.25rem",
    fontSize: "0.95rem",
    color: "#555"
  },
  sourceLink: {
    color: "#555",
    textDecoration: "underline",
    "&:hover, &:focus": {
      color: "#212529"
    }
  },
  author: {
    fontWeight: 700,
    color: "#212529",
    fontStyle: "normal"
  },
  googleCta: {
    textAlign: "center",
    marginTop: "3rem"
  },
  googleButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 14,
    minHeight: 60,
    padding: "8px 28px 8px 8px",
    borderRadius: "3em",
    backgroundColor: "#212529",
    color: "#fff",
    fontSize: "1.1rem",
    fontWeight: 700,
    textDecoration: "none",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.18)",
    transition: "background-color 0.2s, box-shadow 0.2s, transform 0.2s",
    "&:hover, &:focus": {
      backgroundColor: "#000",
      color: "#fff",
      textDecoration: "none",
      boxShadow: "0 10px 24px rgba(0, 0, 0, 0.25)",
      transform: "translateY(-2px)"
    },
    "&:focus-visible": {
      outline: "3px solid #EC9524",
      outlineOffset: 3
    },
    [theme.breakpoints.down("xs")]: {
      gap: 10,
      padding: "6px 20px 6px 6px",
      fontSize: "0.95rem"
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover, &:focus": {
        transform: "none"
      }
    }
  },
  googleBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    borderRadius: "50%",
    backgroundColor: "#fff",
    flexShrink: 0
  },
  externalIcon: {
    color: "#EC9524",
    fontSize: "1.2rem !important"
  },
  googleNote: {
    marginTop: "1rem",
    marginBottom: 0,
    color: "#555",
    fontSize: "0.95rem"
  }
});

const Stars = ({ rating, className }) => (
  <div className={className} role="img" aria-label={`Rated ${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(n =>
      n <= rating
        ? <StarIcon key={n} fontSize="small" aria-hidden="true" />
        : <StarBorderIcon key={n} fontSize="small" aria-hidden="true" />
    )}
  </div>
);

// Google's "G" mark, inline so it needs no extra request.
const GoogleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Reviews = ({ classes }) => {
  // Until approved reviews are added, the section shows just the Google link.
  const reviews = visibleReviews();

  return (
    <section id="reviews" className={classes.section} aria-labelledby="reviews-heading">
      <div className={classes.inner}>
        <h2 id="reviews-heading" className={`h1 ${classes.heading}`}>What Our Customers Are Saying</h2>
        {reviews.length > 0 && <ul className={classes.cards}>
          {reviews.map((review, index) => (
            <li key={index} className={`${classes.card} ${review.placeholder ? classes.placeholder : ""}`}>
              <figure style={{ margin: 0, display: "flex", flexDirection: "column", height: "100%" }}>
                {review.placeholder && <span className={classes.placeholderBadge}>DEV PLACEHOLDER</span>}
                {review.rating && <Stars rating={review.rating} className={classes.stars} />}
                <blockquote className={classes.quote}>{review.quote}</blockquote>
                <figcaption className={classes.attribution}>
                  <cite className={classes.author}>{review.author}</cite>
                  {review.source && (review.url
                    ? <> on <a href={review.url} target="_blank" rel="noopener noreferrer" className={classes.sourceLink}>{review.source}<span className="sr-only"> (opens the original review in a new tab)</span></a></>
                    : <> on {review.source}</>)}
                </figcaption>
              </figure>
            </li>
          ))}
        </ul>}
        <div className={classes.googleCta}>
          <a href={business.reviewsUrl} target="_blank" rel="noopener noreferrer" className={classes.googleButton}>
            <span className={classes.googleBadge}><GoogleIcon /></span>
            {reviews.length > 0 ? "Read more reviews on Google" : "Read our reviews on Google"}
            <OpenInNewIcon aria-hidden="true" className={classes.externalIcon} />
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          <p className={classes.googleNote}>Been by the shack? We'd love to hear what you thought.</p>
        </div>
      </div>
    </section>
  );
};

export default withStyles(styles)(Reviews);
